/*
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

const DB_NAME = "pristy-collab-backups";
const STORE_NAME = "backups";
const DB_VERSION = 1;

let dbInstance = null;

/**
 * Opens the IndexedDB database lazily (single shared connection).
 *
 * @returns {Promise<IDBDatabase>}
 */
const openDB = () => {
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "nodeId" });
      }
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      // Reset instance if the connection is closed unexpectedly
      dbInstance.onclose = () => {
        dbInstance = null;
      };
      resolve(dbInstance);
    };

    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

export default {
  /**
   * Saves a backup of the editor content for a given node.
   *
   * @param {string} nodeId - The Alfresco node ID
   * @param {string} content - The editor content to back up
   * @param {string} sessionId - The collaborative session ID
   * @returns {Promise<void>}
   */
  async saveBackup(nodeId, content, sessionId) {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        store.put({
          nodeId,
          content,
          timestamp: Date.now(),
          sessionId,
        });
        tx.oncomplete = () => resolve();
        tx.onerror = (event) => reject(event.target.error);
      });
    } catch (err) {
      console.warn("[CollabBackup] Failed to save backup:", err);
    }
  },

  /**
   * Loads a backup for a given node.
   *
   * @param {string} nodeId - The Alfresco node ID
   * @returns {Promise<Object|null>} The backup record or null
   */
  async loadBackup(nodeId) {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(nodeId);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = (event) => reject(event.target.error);
      });
    } catch (err) {
      console.warn("[CollabBackup] Failed to load backup:", err);
      return null;
    }
  },

  /**
   * Clears the backup for a given node.
   *
   * @param {string} nodeId - The Alfresco node ID
   * @returns {Promise<void>}
   */
  async clearBackup(nodeId) {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        store.delete(nodeId);
        tx.oncomplete = () => resolve();
        tx.onerror = (event) => reject(event.target.error);
      });
    } catch (err) {
      console.warn("[CollabBackup] Failed to clear backup:", err);
    }
  },
};
